/**
 * The reader's language preference.
 *
 * There are two cookies, and the distinction matters. `echoplay_locale` is the
 * application-wide choice made in Settings, which is the one a developer sets
 * once and expects the whole site — including the EchoEngine portal chrome — to
 * honour. A product's own `<id>_locale` cookie is a narrower override for
 * someone reading one portal in a different language from the rest of the site.
 *
 * Resolution order is therefore: an explicit `?lang=` on the request, then the
 * portal override, then the Settings-wide preference, then English. Putting the
 * query parameter first keeps a shared link reproducible for whoever opens it.
 *
 * A cookie rather than `localStorage` because the portal is server-rendered: the
 * server has to know the language before it emits any markup, or the first paint
 * is in the wrong language and the nav flickers when it corrects itself.
 *
 * Pure and free of `server-only`: the Settings client component writes it and
 * server components read it.
 */

import {
  DEFAULT_CONTENT_LOCALE,
  isContentLocale,
  LOCALE_PARAM,
  type ContentLocale,
} from '../content/constants';

/** Set in Settings; applies across the site. */
export const APP_LOCALE_COOKIE_NAME = 'echoplay_locale';

/** A year: the preference is a setting, not a session detail. */
export const APP_LOCALE_COOKIE_MAX_AGE = 365 * 24 * 60 * 60;

export interface LocaleSources {
  /** The `?lang=` value, if any. */
  param?: unknown;
  /** The product-scoped override cookie, if any. */
  portalCookie?: unknown;
  /** The Settings-wide preference cookie, if any. */
  appCookie?: unknown;
}

export function resolveLocale(sources: LocaleSources): ContentLocale {
  for (const candidate of [sources.param, sources.portalCookie, sources.appCookie]) {
    if (isContentLocale(candidate)) {
      return candidate;
    }
  }

  return DEFAULT_CONTENT_LOCALE;
}

/**
 * The `Set-Cookie` attributes for the Settings preference.
 *
 * Not `httpOnly`, because the Settings page is a client component and has to be
 * able to write it. That is safe: the value is one of two known strings and
 * authorises nothing. It is validated on read anyway, so a tampered cookie
 * yields English rather than anything interesting.
 */
export function appLocaleCookieAttributes(): {
  name: string;
  path: string;
  sameSite: 'lax';
  maxAge: number;
  secure: boolean;
} {
  return {
    name: APP_LOCALE_COOKIE_NAME,
    path: '/',
    sameSite: 'lax',
    maxAge: APP_LOCALE_COOKIE_MAX_AGE,
    secure: process.env.NODE_ENV === 'production',
  };
}

/** Serialises the preference for `document.cookie`. */
export function serializeAppLocaleCookie(locale: ContentLocale): string {
  const { name, path, sameSite, maxAge, secure } = appLocaleCookieAttributes();
  const parts = [`${name}=${locale}`, `Path=${path}`, `SameSite=${sameSite}`, `Max-Age=${maxAge}`];

  if (secure) {
    parts.push('Secure');
  }

  return parts.join('; ');
}

/** Appends `?lang=` to a portal path, preserving any existing query. */
export function withLocaleParam(path: string, locale: ContentLocale): string {
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${LOCALE_PARAM}=${locale}`;
}
